import { createAuthClient, type SessionUser } from "@olgen/api/auth";
import { type QueryClient, queryOptions } from "@tanstack/react-query";

/**
 * Alles, was das Frontend über die Anmeldung weiß und tut.
 *
 * Wie bei `api.ts` gilt: In den Komponenten steht keine Request-Logik. Der
 * Unterschied zu `api.ts` ist nur, dass die Anmeldung nicht über Eden läuft,
 * sondern über BetterAuths eigenen Client — gekapselt in `@olgen/api/auth`.
 *
 * Die Sitzung liegt in TanStack Query und **nur** dort. BetterAuth brächte mit
 * `useSession` einen eigenen Speicher mit; beide nebeneinander wären zwei
 * Wahrheiten über dieselbe Frage, die auseinanderlaufen können. Der
 * Routen-Guard in `_angemeldet.tsx` und die Oberfläche lesen deshalb denselben
 * Eintrag.
 */
const client = createAuthClient({ baseUrl: window.location.origin });

export type { SessionUser };

export const SITZUNG_KEY = ["sitzung"] as const;

export const sitzungQuery = queryOptions({
	queryKey: SITZUNG_KEY,
	queryFn: () => client.getSession(),
	// Nicht angemeldet zu sein ist eine Antwort und kein Fehler. Ein Versuch
	// reicht, sonst wartet jemand auf der Anmeldeseite auf Wiederholungen.
	retry: false,
	// Kurz genug, dass eine abgelaufene Sitzung auffällt, lang genug, dass
	// nicht jeder Seitenwechsel eine Anfrage auslöst.
	staleTime: 30_000,
});

/**
 * Meldet an und sorgt dafür, dass die Sitzung danach frisch gelesen wird.
 *
 * Das Neuladen ist kein Beiwerk: Ohne es stünde im Query-Cache weiter `null`
 * und der Guard schickte den gerade Angemeldeten sofort wieder auf /login.
 *
 * Und es muss `refetchQueries` sein, nicht `invalidateQueries`. Der
 * Unterschied hat uns hier eine Runde gekostet: `invalidateQueries` markiert
 * nur als veraltet und lädt von sich aus nur nach, was gerade eine Komponente
 * abonniert hat. Auf der Anmeldeseite abonniert die Sitzung aber niemand.
 * `ensureQueryData` im Guard gab danach weiter das alte `null` zurück, und
 * der frisch Angemeldete landete wieder auf dem Formular — ohne Fehlermeldung,
 * die Seite tat einfach nichts.
 */
export async function anmelden(args: {
	queryClient: QueryClient;
	email: string;
	password: string;
}): Promise<{ ok: true } | { ok: false; message: string }> {
	const ergebnis = await client.signIn({ email: args.email, password: args.password });

	if (!ergebnis.ok) {
		return ergebnis;
	}

	await args.queryClient.refetchQueries({ queryKey: SITZUNG_KEY });

	return { ok: true };
}

/**
 * Meldet ab und räumt den gesamten Cache leer.
 *
 * Bewusst `clear()` und nicht nur die Sitzung: Was der vorige Benutzer geladen
 * hat, darf der nächste an diesem Rechner nicht mehr sehen.
 */
export async function abmelden(args: { queryClient: QueryClient }): Promise<void> {
	await client.signOut();
	args.queryClient.clear();
}

/** „Passwort vergessen" (T072): Antwortet immer freundlich — verrät nie, ob es die Adresse gibt. */
export function passwortLinkAnfordern(args: { email: string }) {
	return client.passwortLinkAnfordern(args);
}

/** Setzt das Passwort mit dem Token aus der Mail (Zugangs-Mail und Reset). */
export function passwortSetzen(args: { token: string; neuesPasswort: string }) {
	return client.passwortSetzen(args);
}

/** Profil: Anzeigenamen ändern — danach die Sitzung neu laden, damit die Oberfläche ihn zeigt. */
export async function nameAendern(args: { name: string; queryClient: QueryClient }) {
	const ergebnis = await client.nameAendern({ name: args.name });
	if (ergebnis.ok) await args.queryClient.refetchQueries({ queryKey: SITZUNG_KEY });
	return ergebnis;
}

/** Profil: Passwort ändern (altes Passwort bestätigen). */
export function passwortAendern(args: { aktuellesPasswort: string; neuesPasswort: string }) {
	return client.passwortAendern(args);
}
